/**
 * The hammer's one press, read three ways. A press on a fastener head
 * starts here and ends as exactly one of: a swing (released quickly,
 * hand still), a pull (held until the claw sets, then dragged along the
 * lever), or nothing. Between the three lie dead zones on purpose — a
 * release that came too late to be a swing but too early for the claw
 * does nothing, a quick release with the hand moving swings and misses,
 * a long hold with the hand off the head lowers the hammer unused — so
 * a near miss never becomes the other intent.
 *
 * Swings have a rhythm. A full blow lands only once the hammer has come
 * back up from the last one (`windUpMs`); a swing before that glances
 * off and restarts the wind-up, so the fastest way to set a nail is to
 * time the taps, not to spam them. It takes `blowsToSet` full blows to
 * set a nail; a powered driver sets a screw in one and never winds up.
 *
 * The pull measures travel only from where the claw bit: whatever the
 * hand did before the hold crossed the threshold is discarded. Progress
 * is the travel's component along the lever, so the nail slides back if
 * the hand comes back, and it pops the moment it clears — no release
 * needed. Releasing before that slides it home.
 *
 * Pure: every input carries its own clock and positions (screen px),
 * so the whole thing is unit-tested without a bench. The views feed it
 * mouse down, moves, ticks, and mouse up, and act on the outcomes; the
 * cursor draws from `hammerCursor`. Performance affects speed, never
 * quality (docs/bench-work.md decision 2): a bad rhythm costs blows,
 * never the nail.
 */

export interface HammerTuning {
  /** A release inside this many ms of the press is a swing. */
  readonly tapWindowMs: number;
  /** Holding past this many ms turns the hammer over into the claw. */
  readonly clawSetMs: number;
  /** Hand travel past this many px during a press counts as moving. */
  readonly travelSlopPx: number;
  /** How long after a swing the hammer takes to come back up. */
  readonly windUpMs: number;
  /** Full blows that set one fastener. */
  readonly blowsToSet: number;
  /** How far along the lever the claw has to travel to clear a nail. */
  readonly pullDistancePx: number;
}

/** A claw hammer: three timed blows a nail, the claw for pulling. */
export const HAMMER_TUNING: HammerTuning = {
  tapWindowMs: 180,
  clawSetMs: 250,
  travelSlopPx: 6,
  windUpMs: 300,
  blowsToSet: 3,
  pullDistancePx: 40,
};

/** A powered driver: one press seats a screw, nothing to wind up, and
 * the same hold-and-drag backs one out. */
export const DRIVER_TUNING: HammerTuning = {
  ...HAMMER_TUNING,
  windUpMs: 0,
  blowsToSet: 1,
};

/** The claw's lever on screen for a nail driven straight down: up and
 * to the right, the way a right hand levers (the pallet pry's line). */
export const CLAW_LEVER = {
  dx: 16 / Math.hypot(16, 22),
  dy: -22 / Math.hypot(16, 22),
};

export interface HammerPress {
  /** What the press is on — the caller's key for the head. */
  readonly target: string;
  readonly startMs: number;
  readonly startX: number;
  readonly startY: number;
  /** The hand travelled past the slop at some point during the press. */
  readonly moved: boolean;
  /** Set once the hold crossed the claw threshold: where the claw bit,
   * and how far along the lever the nail has come (0..1). */
  readonly claw: {
    readonly x: number;
    readonly y: number;
    readonly progress: number;
  } | null;
  /** Held to the claw threshold with the hand off the head: this press
   * ends in nothing. */
  readonly lapsed: boolean;
}

export interface HammerState {
  readonly press: HammerPress | null;
  /** When the hammer last came down — blow, glance, or miss. The
   * wind-up counts from here. */
  readonly swungMs: number;
}

export const HAMMER_AT_REST: HammerState = { press: null, swungMs: -Infinity };

export type HammerOutcome =
  /** A full blow: the hammer was back up and the hand was still. */
  | { readonly kind: "blow"; readonly target: string }
  /** A swing before the hammer was back up: it glances off. */
  | { readonly kind: "glance"; readonly target: string }
  /** A swing with the hand moving: it misses the head. */
  | { readonly kind: "miss"; readonly target: string }
  /** The hold crossed the threshold with the hand on the head. */
  | { readonly kind: "clawSet"; readonly target: string }
  /** The claw's travel cleared the nail. */
  | { readonly kind: "pulled"; readonly target: string }
  /** Released with the nail part way out: it slides home. */
  | { readonly kind: "slidBack"; readonly target: string }
  /** Released in a dead zone: the hammer lowers unused. */
  | { readonly kind: "lowered"; readonly target: string };

/** How far the hammer has come back up since it last came down, 0..1 —
 * 1 is ready for a full blow. Always ready with nothing to wind up. */
export function windUp(
  state: HammerState,
  nowMs: number,
  tuning: HammerTuning,
): number {
  if (tuning.windUpMs <= 0) return 1;
  return Math.min(1, Math.max(0, (nowMs - state.swungMs) / tuning.windUpMs));
}

/** The press begins: the hammer is cocked over this head. */
export function hammerDown(
  state: HammerState,
  target: string,
  x: number,
  y: number,
  nowMs: number,
): HammerState {
  return {
    ...state,
    press: {
      target,
      startMs: nowMs,
      startX: x,
      startY: y,
      moved: false,
      claw: null,
      lapsed: false,
    },
  };
}

/**
 * The hand moved during a press. Before the claw sets this only notes
 * that it moved; after, it measures the pull along the lever and pops
 * the nail once it clears.
 */
export function hammerMove(
  state: HammerState,
  x: number,
  y: number,
  lever: { readonly dx: number; readonly dy: number },
  tuning: HammerTuning,
): { state: HammerState; outcome: HammerOutcome | null } {
  const press = state.press;
  if (!press) return { state, outcome: null };
  if (press.claw) {
    const along = (x - press.claw.x) * lever.dx + (y - press.claw.y) * lever.dy;
    const progress = Math.max(0, along / tuning.pullDistancePx);
    if (progress >= 1) {
      return {
        state: { ...state, press: null },
        outcome: { kind: "pulled", target: press.target },
      };
    }
    return {
      state: {
        ...state,
        press: { ...press, claw: { ...press.claw, progress } },
      },
      outcome: null,
    };
  }
  const moved =
    press.moved ||
    Math.hypot(x - press.startX, y - press.startY) > tuning.travelSlopPx;
  return {
    state:
      moved === press.moved ? state : { ...state, press: { ...press, moved } },
    outcome: null,
  };
}

/**
 * Time passes with the button held. Once the hold crosses the claw
 * threshold the hammer turns over — if the hand is still on the head.
 * Off it, the press lapses: it will end in nothing.
 */
export function hammerHold(
  state: HammerState,
  nowMs: number,
  x: number,
  y: number,
  overTarget: boolean,
  tuning: HammerTuning,
): { state: HammerState; outcome: HammerOutcome | null } {
  const press = state.press;
  if (!press || press.claw || press.lapsed) return { state, outcome: null };
  if (nowMs - press.startMs < tuning.clawSetMs) return { state, outcome: null };
  if (!overTarget) {
    return {
      state: { ...state, press: { ...press, lapsed: true } },
      outcome: null,
    };
  }
  return {
    state: { ...state, press: { ...press, claw: { x, y, progress: 0 } } },
    outcome: { kind: "clawSet", target: press.target },
  };
}

/** The button comes up: the press resolves. */
export function hammerUp(
  state: HammerState,
  nowMs: number,
  tuning: HammerTuning,
): { state: HammerState; outcome: HammerOutcome | null } {
  const press = state.press;
  if (!press) return { state, outcome: null };
  const released = { ...state, press: null };
  if (press.claw) {
    return {
      state: released,
      outcome: { kind: "slidBack", target: press.target },
    };
  }
  if (press.lapsed || nowMs - press.startMs > tuning.tapWindowMs) {
    return {
      state: released,
      outcome: { kind: "lowered", target: press.target },
    };
  }
  // A swing. It comes down whatever it hits, so the wind-up restarts.
  const swung = { ...released, swungMs: nowMs };
  if (press.moved) {
    return { state: swung, outcome: { kind: "miss", target: press.target } };
  }
  if (windUp(state, nowMs, tuning) < 1) {
    return { state: swung, outcome: { kind: "glance", target: press.target } };
  }
  return { state: swung, outcome: { kind: "blow", target: press.target } };
}

/** What the hand's hammer is doing, for the cursor to draw. */
export interface HammerCursor {
  readonly phase: "rest" | "cocked" | "claw";
  /** 0..1, how far back up the hammer has come; 1 is ready. */
  readonly windUp: number;
  /** 0..1 along the lever while the claw is pulling. */
  readonly pull: number;
}

export function hammerCursor(
  state: HammerState,
  nowMs: number,
  tuning: HammerTuning,
): HammerCursor {
  const press = state.press;
  return {
    phase: press ? (press.claw ? "claw" : "cocked") : "rest",
    windUp: windUp(state, nowMs, tuning),
    pull: press?.claw?.progress ?? 0,
  };
}
